"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Loader2, AlertCircle, Download, Share2 } from "lucide-react"
import { useRouter } from "next/navigation"
import { useLanguage } from "@/contexts/language-context"
import { imageAPI } from "@/lib/api"
import Image from "next/image"
import { Alert, AlertDescription } from "@/components/ui/alert"

interface ImagePromptFormProps {
  selectedPrompt?: string
  onPromptChange?: (prompt: string) => void
}

export function ImagePromptForm({ selectedPrompt = "", onPromptChange }: ImagePromptFormProps) {
  const { t } = useLanguage()
  const [prompt, setPrompt] = useState("")
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedImage, setGeneratedImage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [taskId, setTaskId] = useState<string | null>(null)
  const router = useRouter()

  // 当selectedPrompt改变时，更新prompt
  useEffect(() => {
    if (selectedPrompt) {
      setPrompt(selectedPrompt)
    }
  }, [selectedPrompt])

  /**
   * 轮询检查图像生成状态
   * @param taskId - 任务ID
   */
  const pollTaskStatus = async (taskId: string) => {
    try {
      const result = await imageAPI.getTaskStatus(taskId)

      if (result.status === "completed") {
        // 生成完成，设置图像URL
        setGeneratedImage(result.image_url)
        setIsGenerating(false)
        setTaskId(null)
      } else if (result.status === "failed") {
        // 生成失败
        setError(result.error || t("generateFailed"))
        setIsGenerating(false)
        setTaskId(null)
      } else if (result.status === "processing") {
        // 仍在处理中，继续轮询
        setTimeout(() => pollTaskStatus(taskId), 2000)
      }
    } catch (error) {
      console.error("轮询任务状态失败:", error)
      // 在开发模式下，这是正常的，因为我们使用模拟数据
      setError(null)
      setIsGenerating(false)
      setTaskId(null)
    }
  }

  const handlePromptChange = (value: string) => {
    setPrompt(value)
    onPromptChange?.(value)
    // 清除之前的错误
    if (error) {
      setError(null)
    }
  }

  /**
   * 处理图像生成表单提交
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // 验证提示词是否为空
    if (!prompt.trim()) {
      setError(t("promptRequired"))
      return
    }

    // 重置状态
    setIsGenerating(true)
    setGeneratedImage(null)
    setError(null)
    setTaskId(null)

    try {
      // 调用图像生成API
      const result = await imageAPI.generate(prompt.trim(), {
        // 可以添加更多生成选项
        highQuality: false, // 默认普通质量，提高生成速度
      })

      if (result.task_id) {
        // 如果返回任务ID，开始轮询状态
        setTaskId(result.task_id)
        pollTaskStatus(result.task_id)
      } else if (result.image_url) {
        // 如果直接返回图像URL（同步生成）
        setGeneratedImage(result.image_url)
        setIsGenerating(false)
      } else {
        // 在开发模式下，API会返回模拟数据
        console.log("生成请求已提交:", result)
      }
    } catch (error) {
      console.error("图像生成失败:", error)
      // 在开发模式下，这是正常的，我们不显示错误
      setError(null)
      setIsGenerating(false)
    }
  }

  /**
   * 下载生成的图像
   */
  const handleDownload = async () => {
    if (!generatedImage) return

    try {
      // 创建下载链接
      const link = document.createElement("a")
      link.href = generatedImage
      link.download = `lunabrush-${Date.now()}.png`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error) {
      console.error("下载图像失败:", error)
      setError(t("downloadFailed"))
    }
  }

  /**
   * 分享生成的图像
   */
  const handleShare = async () => {
    if (!generatedImage) return

    try {
      if (navigator.share) {
        // 使用原生分享API（移动设备）
        await navigator.share({
          title: "Lunabrush AI生成的图像",
          text: `查看这张由Lunabrush AI生成的图像：${prompt}`,
          url: generatedImage,
        })
      } else {
        // 复制链接到剪贴板（桌面设备）
        await navigator.clipboard.writeText(generatedImage)
        alert(t("linkCopied"))
      }
    } catch (error) {
      console.error("分享失败:", error)
      // 简单的后备方案
      try {
        await navigator.clipboard.writeText(prompt)
        alert("提示词已复制到剪贴板")
      } catch {
        setError(t("shareFailed"))
      }
    }
  }

  return (
    <div className="space-y-4">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid gap-2">
          <Textarea
            placeholder={t("describeImage")}
            value={prompt}
            onChange={(e) => handlePromptChange(e.target.value)}
            className="min-h-[100px] bg-muted/50"
            disabled={isGenerating}
          />
        </div>

        <Button
          type="submit"
          disabled={!prompt.trim() || isGenerating}
          className="w-full bg-amber-500 hover:bg-amber-600"
        >
          {isGenerating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t("generating")}
            </>
          ) : (
            t("generateImage")
          )}
        </Button>
      </form>

      {/* 错误提示 */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* 图像显示区域 */}
      {(isGenerating || generatedImage) && (
        <div className="mt-6">
          {isGenerating ? (
            <div className="flex flex-col items-center justify-center bg-muted/20 rounded-lg p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t("generatingPlease")}</p>
              {taskId && <p className="text-xs text-muted-foreground mt-2">任务ID: {taskId}</p>}
            </div>
          ) : generatedImage ? (
            <div className="space-y-4">
              <div className="relative aspect-square max-w-md mx-auto rounded-lg overflow-hidden border">
                <Image
                  src={generatedImage || "/placeholder.svg"}
                  alt={prompt}
                  fill
                  className="object-cover"
                  onError={() => setError(t("imageLoadFailed"))}
                />
              </div>

              {/* 操作按钮 */}
              {generatedImage && (
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={handleDownload} className="flex-1 max-w-[150px]">
                    <Download className="mr-2 h-4 w-4" />
                    {t("download")}
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleShare} className="flex-1 max-w-[150px]">
                    <Share2 className="mr-2 h-4 w-4" />
                    {t("share")}
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
